import { protocolFor, PROTOCOL_VERSION } from 'features/integrations/healAgentProtocol';
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ExternalLink, Loader } from 'lucide-react';
import { PiSealCheckDuotone } from 'react-icons/pi';
import axiosInstance from 'shared/services/api/axiosInstance';
import { useTeam } from 'shared/context/teamContext';
import { CodeBox, INPUT_CLS, IntegrationHero, ConnectedCard, Wizard, LoadingRow } from './connectWizard';


// ─── Page ─────────────────────────────────────────────────────────────────────


/**
 * The instructions live in the customer's automation, not in our request — so
 * changing them here changes nothing until someone re-copies the text. This is
 * the only place that can say so, and it reads the version the last real run
 * reported rather than anything we store at setup.
 */

/**
 * An agent whose Trodo connector is authenticated for a different team than the
 * one triggering the heal is the most common first-run failure — the run dies
 * on get_issue_details returning not-found. We cannot inspect the customer's
 * connector, so this reports the half that IS knowable: whether this team has
 * any live MCP credential for an agent to have been configured with. None means
 * the failure is certain rather than likely.
 */
function McpReadinessNotice({ connection, mcpTo }) {
  if (!connection || connection === true || connection.mcp_ready !== false) return null;
  return (
    <div className="mb-3 rounded-md border border-[color:var(--feedback-warning-bg)] bg-[color:var(--feedback-warning-bg)] p-2.5 text-[11px] text-[color:var(--feedback-warning)]">
      This team has no active Trodo MCP key, so the agent cannot read the issue it is
      sent and every heal will fail. Create one under{' '}
      <Link to={mcpTo} className="font-medium underline underline-offset-2">Settings → MCP</Link>{' '}
      and make sure your automation&apos;s Trodo connector uses it.
    </div>
  );
}

function ProtocolVersionNotice({ connection }) {
  if (!connection || connection === true) return null;
  const seen = connection.last_protocol_version;
  if (seen == null) {
    // Either it has never run, or it ran on a prompt too old to report a
    // version. Both mean "re-copy the text below" — and saying nothing here is
    // how an automation quietly keeps running last month's instructions.
    return (
      <div className="mb-3 rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] p-2.5 text-[11px] text-[color:var(--text-muted)]">
        No run has reported a protocol version yet. If you set this up before
        the current instructions, re-copy the prompt below into your automation.
      </div>
    );
  }
  if (seen >= PROTOCOL_VERSION) return null;
  return (
    <div className="mb-3 rounded-md border border-[color:var(--feedback-warning-bg)] bg-[color:var(--feedback-warning-bg)] p-2.5 text-[11px] text-[color:var(--feedback-warning)]">
      Your automation is running protocol v{seen}; the current version is
      v{PROTOCOL_VERSION}. Re-copy the prompt below: older instructions skip
      the replay and evidence steps, so their fixes arrive unverified.
    </div>
  );
}

export default function CursorIntegrationPage({ teamId: teamIdProp }) {
  const { team: teamSlug } = useParams();
  const { selectedTeam } = useTeam();
  const teamId = teamIdProp || selectedTeam?.id || selectedTeam?._id;
  const teamForIntegrationsPath = teamSlug || selectedTeam?.name || '';
  // MCP keys moved out of Integrations into Settings > Project > MCP.
  const mcpTo = teamForIntegrationsPath
    ? `/${encodeURIComponent(teamForIntegrationsPath)}/settings/mcp`
    : '/settings/mcp';

  const [connection, setConnection] = useState(null);
  const [loadingConn, setLoadingConn] = useState(true);
  const [step, setStep] = useState(0);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [cursorApiKey, setCursorApiKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [verifyError, setVerifyError] = useState('');
  const [verifyOk, setVerifyOk] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  useEffect(() => {
    if (!teamId) return;
    axiosInstance.get(`/integrations/cursor?teamId=${teamId}`)
      .then(r => setConnection(r.data?.connected ? r.data : false))
      .catch(() => setConnection(false))
      .finally(() => setLoadingConn(false));
  }, [teamId]);

  const handleSave = useCallback(async () => {
    setSaveError('');
    if (!webhookUrl.startsWith('https://')) { setSaveError('Webhook URL must start with https://'); return; }
    if (!cursorApiKey.trim()) { setSaveError('Cursor API key is required'); return; }
    setSaving(true);
    try {
      await axiosInstance.post(`/integrations/cursor?teamId=${teamId}`, { webhook_url: webhookUrl, cursor_api_key: cursorApiKey || undefined });
      setConnection({ connected: true });
      setVerifyOk(false);
      setCursorApiKey('');
    } catch (err) { setSaveError(err.response?.data?.error || 'Failed to save'); }
    finally { setSaving(false); }
  }, [teamId, webhookUrl, cursorApiKey]);

  const handleVerify = useCallback(async () => {
    setVerifyError(''); setVerifyOk(false); setVerifying(true);
    try {
      await axiosInstance.post(`/integrations/cursor/verify?teamId=${teamId}`, { webhook_url: webhookUrl || undefined, cursor_api_key: cursorApiKey || undefined });
      setVerifyOk(true);
      setConnection(c => c ? { ...c, verified_at: new Date().toISOString() } : c);
    } catch (err) { setVerifyError(err.response?.data?.error || 'Verification failed: check the URL and try again'); }
    finally { setVerifying(false); }
  }, [teamId, webhookUrl, cursorApiKey]);

  const handleDisconnect = useCallback(async () => {
    setDisconnecting(true);
    try {
      await axiosInstance.delete(`/integrations/cursor?teamId=${teamId}`);
      setConnection(false); setStep(0);
      setWebhookUrl(''); setCursorApiKey(''); setVerifyOk(false);
    } catch {}
    finally { setDisconnecting(false); }
  }, [teamId]);

  const isConnected = connection && connection.connected;

  const steps = [
    {
      title: 'Create the automation in Cursor',
      body: (
        <div>
          <p className="mb-3 text-[13px] text-[color:var(--text-muted)]">
            Set up a webhook-triggered automation. If Cursor's labels differ slightly, pick the closest option.
          </p>
          <ol className="list-decimal space-y-2.5 pl-4 text-[13px] text-[color:var(--text-primary)] marker:font-medium marker:text-[color:var(--text-muted)]">
            <li className="pl-1">
              Open{' '}
              <a href="https://cursor.com/automations" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-0.5 font-medium text-[color:var(--brand-primary)] underline-offset-2 hover:underline">
                cursor.com/automations <ExternalLink size={11} className="shrink-0" />
              </a>
              {' '}and click <strong>New automation</strong>.
            </li>
            <li className="pl-1">Name your automation.</li>
            <li className="pl-1">Open <strong>Triggers</strong>, choose <strong>Webhook</strong>, then pick your <strong>repository</strong> and <strong>main</strong> branch.</li>
            <li className="pl-1">Choose the <strong>model</strong> this automation should use.</li>
          </ol>
        </div>
      ),
    },
    {
      title: 'Paste the heal agent system prompt',
      body: (
        <div>
          <p className="mb-2 text-[13px] text-[color:var(--text-muted)]">
            In the <strong>Instructions</strong> field, paste the full prompt below. It only pushes a fresh branch —
            it never opens a PR and never merges.
          </p>
          <McpReadinessNotice connection={connection} mcpTo={mcpTo} />
            <ProtocolVersionNotice connection={connection} />
          <CodeBox label="System prompt (copy into Cursor)" copyText={protocolFor('cursor')}>
            {protocolFor('cursor')}
          </CodeBox>
        </div>
      ),
      continueLabel: 'Pasted: next step',
    },
    {
      title: 'Connect the Trodo MCP server',
      body: (
        <div>
          <p className="mb-2 text-[13px] text-[color:var(--text-muted)]">
            Use <strong>Add tool</strong> or <strong>Add MCP</strong>, then add the <strong>Trodo</strong> server so the
            automation can pull issue context. Then click <strong>Create</strong> to save the automation.
          </p>
          <p className="rounded-md border border-[color:var(--border-default)] bg-surface px-3 py-2 text-[12px] leading-snug text-[color:var(--text-muted)]">
            <span className="font-semibold text-[color:var(--text-primary)]">Note.</span>{' '}
            If Trodo MCP isn't connected yet, open{' '}
            <Link to={mcpTo} className="font-medium text-[color:var(--brand-primary)] underline-offset-2 hover:underline">
              Settings → MCP
            </Link>{' '}
            first, then return here and attach Trodo to this automation.
          </p>
        </div>
      ),
      continueLabel: 'Connected: next step',
    },
    {
      title: 'Register the webhook URL and API key',
      final: true,
      body: (
        <div>
          <p className="mb-3 text-[13px] text-[color:var(--text-muted)]">
            Copy the webhook URL from your automation's <strong>Triggers</strong> tab, and get your API key from{' '}
            <a href="https://cursor.com/settings" target="_blank" rel="noopener noreferrer"
              className="font-medium text-[color:var(--brand-primary)] underline-offset-2 hover:underline">
              Cursor Settings → API keys
            </a>.
          </p>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">
                Webhook URL
              </label>
              <input
                type="url"
                value={webhookUrl}
                onChange={e => { setWebhookUrl(e.target.value); setSaveError(''); }}
                placeholder="https://api2.cursor.sh/automations/webhook/…"
                className={INPUT_CLS}
              />
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">
                Cursor API key
              </label>
              <input
                type="password"
                value={cursorApiKey}
                onChange={e => { setCursorApiKey(e.target.value); setSaveError(''); }}
                placeholder="Cursor API key (required for auth)"
                className={INPUT_CLS}
                autoComplete="new-password"
              />
            </div>
          </div>
          {saveError && <p className="mt-1.5 text-[12px] text-danger">{saveError}</p>}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !webhookUrl || !cursorApiKey}
              className="inline-flex h-8 items-center rounded-md bg-brand px-4 text-[13px] font-semibold text-ink-onBrand transition-colors hover:bg-brand-hover disabled:opacity-40"
            >
              {saving ? 'Saving…' : 'Save & connect'}
            </button>
            {webhookUrl && (
              <button
                type="button"
                onClick={handleVerify}
                disabled={verifying}
                className="inline-flex h-8 items-center rounded-md border border-[color:var(--border-default)] px-3 text-[13px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover disabled:opacity-40"
              >
                {verifying ? <><Loader size={13} className="mr-1.5 animate-spin" /> Verifying…</> : 'Verify connection'}
              </button>
            )}
            {verifyOk && (
              <span className="flex items-center gap-1 text-[12px] text-success">
                <PiSealCheckDuotone size={13} /> Webhook reachable
              </span>
            )}
            {verifyError && <span className="text-[12px] text-danger">{verifyError}</span>}
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6">
      <IntegrationHero
        iconSrc={`${(process.env.PUBLIC_URL || '').replace(/\/?$/, '')}/icons/cursor.svg`}
        title="Cursor"
        connected={isConnected}
      />

      {loadingConn ? (
        <LoadingRow />
      ) : isConnected ? (
        <div>
          <ConnectedCard onDisconnect={handleDisconnect} disconnecting={disconnecting} />
          {/* The prompt has to stay reachable AFTER connecting. Someone already
              set up is precisely the person running an outdated one, and until
              now the only copy lived inside the setup wizard they will never
              open again. */}
          <div className="mt-4">
            <McpReadinessNotice connection={connection} mcpTo={mcpTo} />
            <ProtocolVersionNotice connection={connection} />
            <CodeBox label="System prompt: re-copy this into your Cursor automation if it has changed" copyText={protocolFor('cursor')}>
              {protocolFor('cursor')}
            </CodeBox>
          </div>
        </div>
      ) : (
        <div>
          <p className="mb-3 text-[13px] text-[color:var(--text-muted)]">
            Follow these steps to connect a Cursor Background Agent for Heal. It takes about a minute.
          </p>
          <Wizard steps={steps} current={step} setCurrent={setStep} />
        </div>
      )}
    </div>
  );
}
